/**
 * TanStack Query hooks for Sphero BPS Projects API
 */

import { useMutation, useQueryClient, UseMutationOptions } from '@tanstack/react-query';
import { projectsApi, ProjectsApiError } from './projectsApi';
import type { CreateProjectResponse } from './types';

// Query Keys
export const projectKeys = {
  all: ['projects'] as const,
  byShareCode: (shareCode: string) => ['projects', shareCode] as const,
};

/**
 * Hook to create a new project
 */
export function useCreateProject(
  options?: UseMutationOptions<CreateProjectResponse, ProjectsApiError, { projectString: string; pin: string; title?: string }>
) {
  const queryClient = useQueryClient();

  return useMutation<CreateProjectResponse, ProjectsApiError, { projectString: string; pin: string; title?: string }>({
    mutationFn: ({ projectString, pin, title }) => projectsApi.create(projectString, pin, title),
    onSuccess: () => {
      // Invalidate projects list if we're caching it
      queryClient.invalidateQueries({ queryKey: projectKeys.all });
    },
    ...options,
  });
}

/**
 * Hook to delete a project from the server by share code and collaborator PIN
 */
export function useDeleteProject(options?: UseMutationOptions<void, ProjectsApiError, { shareCode: string; pin: string }>) {
  const queryClient = useQueryClient();

  return useMutation<void, ProjectsApiError, { shareCode: string; pin: string }>({
    mutationFn: ({ shareCode, pin }) => projectsApi.delete(shareCode, pin),
    onSuccess: (_data, { shareCode }) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.all });
      queryClient.removeQueries({ queryKey: projectKeys.byShareCode(shareCode) });
    },
    ...options,
  });
}
